import { useState, useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Loader2, Plus, UserPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFinance } from '@/contexts/FinanceContext';
import { usePatients, useCreatePatient } from './usePatients';
import { useCreateTreatmentPackage } from './useTreatmentPackages';
import { calculateInstallments } from './types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function parseBrl(s: string): number {
  const cleaned = s
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function formatBrl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function PackageModal({ open, onOpenChange }: Props) {
  const { accounts, categories, costCenters } = useFinance();
  const { data: patients = [] } = usePatients();
  const createPatient = useCreatePatient();
  const createPackage = useCreateTreatmentPackage();

  // Patient picker / inline create
  const [mode, setMode] = useState<'pick' | 'create'>('pick');
  const [patientId, setPatientId] = useState<string>('');
  const [newPatient, setNewPatient] = useState({ name: '', phone: '', cpf: '', email: '' });

  // Package fields
  const [description, setDescription] = useState('');
  const [totalAmountStr, setTotalAmountStr] = useState('');
  const [downPaymentStr, setDownPaymentStr] = useState('');
  const [numberOfInstallments, setNumberOfInstallments] = useState('12');
  const today = format(new Date(), 'yyyy-MM-dd');
  const [startDate, setStartDate] = useState(today);
  const [accountId, setAccountId] = useState('');
  const [categoryId, setCategoryId] = useState<string>('none');
  const [costCenterId, setCostCenterId] = useState<string>('none');
  const [notes, setNotes] = useState('');

  const incomeCategories = useMemo(
    () => categories.filter((c) => c.type === 'income'),
    [categories],
  );
  const activeAccounts = useMemo(() => accounts.filter((a) => a.isActive), [accounts]);
  const activeCostCenters = useMemo(() => costCenters.filter((cc) => cc.isActive), [costCenters]);

  const totalAmount = parseBrl(totalAmountStr);
  const downPayment = parseBrl(downPaymentStr);
  const installmentsCount = Math.max(0, parseInt(numberOfInstallments) || 0);

  const summary = useMemo(() => {
    if (totalAmount <= 0 || installmentsCount === 0) return null;
    return calculateInstallments(
      totalAmount,
      downPayment,
      installmentsCount,
      new Date(`${startDate}T00:00:00`),
    );
  }, [totalAmount, downPayment, installmentsCount, startDate]);

  const valid =
    !!description.trim() &&
    totalAmount > 0 &&
    downPayment >= 0 &&
    downPayment < totalAmount &&
    installmentsCount > 0 &&
    !!accountId &&
    (mode === 'pick' ? !!patientId : !!newPatient.name.trim());

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (open) {
      setMode('pick');
      setPatientId('');
      setNewPatient({ name: '', phone: '', cpf: '', email: '' });
      setDescription('');
      setTotalAmountStr('');
      setDownPaymentStr('');
      setNumberOfInstallments('12');
      setStartDate(today);
      setAccountId(activeAccounts[0]?.id ?? '');
      setCategoryId('none');
      setCostCenterId('none');
      setNotes('');
    }
  }, [open, activeAccounts, today]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSubmit = async () => {
    let finalPatientId = patientId;
    if (mode === 'create') {
      try {
        const created = await createPatient.mutateAsync({
          name: newPatient.name,
          phone: newPatient.phone,
          cpf: newPatient.cpf,
          email: newPatient.email,
        });
        finalPatientId = created.id;
      } catch {
        return;
      }
    }
    try {
      await createPackage.mutateAsync({
        patientId: finalPatientId,
        description,
        totalAmount,
        downPayment,
        numberOfInstallments: installmentsCount,
        startDate,
        accountId,
        categoryId: categoryId === 'none' ? null : categoryId,
        costCenterId: costCenterId === 'none' ? null : costCenterId,
        notes,
      });
      onOpenChange(false);
    } catch {
      // toast já mostrou
    }
  };

  const isPending = createPatient.isPending || createPackage.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">Novo pacote de tratamento</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* PACIENTE */}
          <div className="space-y-2 rounded-lg border border-border p-3">
            <Label className="text-sm font-semibold">Paciente</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={mode === 'pick' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMode('pick')}
              >
                Selecionar existente
              </Button>
              <Button
                type="button"
                variant={mode === 'create' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMode('create')}
              >
                <UserPlus size={14} className="mr-1.5" />
                Novo paciente
              </Button>
            </div>

            {mode === 'pick' ? (
              <Select value={patientId} onValueChange={setPatientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Escolha o paciente" />
                </SelectTrigger>
                <SelectContent>
                  {patients.length === 0 ? (
                    <div className="p-2 text-center text-xs text-muted-foreground">
                      Nenhum paciente cadastrado.
                    </div>
                  ) : (
                    patients.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                        {p.phone && <span className="text-muted-foreground"> · {p.phone}</span>}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Nome *"
                  value={newPatient.name}
                  onChange={(e) => setNewPatient((p) => ({ ...p, name: e.target.value }))}
                />
                <Input
                  placeholder="CPF"
                  value={newPatient.cpf}
                  onChange={(e) => setNewPatient((p) => ({ ...p, cpf: e.target.value }))}
                />
                <Input
                  placeholder="Telefone"
                  value={newPatient.phone}
                  onChange={(e) => setNewPatient((p) => ({ ...p, phone: e.target.value }))}
                />
                <Input
                  placeholder="E-mail"
                  type="email"
                  value={newPatient.email}
                  onChange={(e) => setNewPatient((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
            )}
          </div>

          {/* PACOTE */}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="pkg-desc">Descrição *</Label>
              <Input
                id="pkg-desc"
                placeholder="Ex: Aparelho ortodôntico — 24 meses"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="pkg-total">Valor total *</Label>
                <Input
                  id="pkg-total"
                  placeholder="R$ 0,00"
                  value={totalAmountStr}
                  onChange={(e) => setTotalAmountStr(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pkg-down">Entrada</Label>
                <Input
                  id="pkg-down"
                  placeholder="R$ 0,00"
                  value={downPaymentStr}
                  onChange={(e) => setDownPaymentStr(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pkg-installments">Parcelas *</Label>
                <Input
                  id="pkg-installments"
                  type="number"
                  min="1"
                  max="120"
                  value={numberOfInstallments}
                  onChange={(e) => setNumberOfInstallments(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="pkg-start">Início *</Label>
                <Input
                  id="pkg-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Conta de recebimento *</Label>
                <Select value={accountId} onValueChange={setAccountId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeAccounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Categoria (receita)</Label>
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— sem categoria —</SelectItem>
                    {incomeCategories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Centro de custo</Label>
                <Select value={costCenterId} onValueChange={setCostCenterId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— sem centro de custo —</SelectItem>
                    {activeCostCenters.map((cc) => (
                      <SelectItem key={cc.id} value={cc.id}>
                        {cc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pkg-notes">Observações</Label>
              <Textarea
                id="pkg-notes"
                rows={2}
                placeholder="Detalhes do tratamento, condições, etc."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          {/* PREVIEW */}
          {summary && summary.installments.length > 0 && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-primary">
                  {summary.installments.length} lançamento(s) — total {formatBrl(summary.total)}
                </span>
                {summary.installmentAmount > 0 && (
                  <span className="text-xs text-muted-foreground">
                    Parcela: {formatBrl(summary.installmentAmount)}
                  </span>
                )}
              </div>
              <div className="max-h-48 overflow-y-auto rounded border border-border bg-white">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-secondary/50">
                    <tr className="text-left font-medium text-muted-foreground">
                      <th className="px-2 py-1.5">#</th>
                      <th className="px-2 py-1.5">Data</th>
                      <th className="px-2 py-1.5">Descrição</th>
                      <th className="px-2 py-1.5 text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.installments.map((it) => (
                      <tr key={it.index} className="border-t border-border/50">
                        <td className="px-2 py-1.5 text-muted-foreground">{it.index}</td>
                        <td className="px-2 py-1.5">
                          {format(it.date, 'dd/MM/yyyy', { locale: ptBR })}
                        </td>
                        <td className="px-2 py-1.5">{it.label}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {formatBrl(it.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {totalAmount > 0 && downPayment >= totalAmount && (
            <p className="rounded border border-expense/30 bg-expense/5 p-2 text-xs text-expense">
              Entrada não pode ser maior ou igual ao total.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!valid || isPending}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {isPending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Criando…
              </>
            ) : (
              <>
                <Plus size={14} className="mr-2" />
                Criar pacote
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
